import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Brain, Eye, Play, CheckCircle, XCircle,
  AlertTriangle, Loader2, ArrowRight, ArrowLeft, Trash2, Undo2,
  RefreshCw, Clock, Download, ChevronDown, X
} from 'lucide-react';
import api from '../lib/api';

// ─── Step 1: Upload ───
function UploadStep({ targets, onUploaded }) {
  const [target, setTarget] = useState('leads');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    const ok = file && /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!ok) {
      toast.error('Please upload a CSV or Excel (.xlsx) file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('target', target);

      const res = await api.post('/api/data-import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${file.name} (${res.data.total_rows} rows)`);
      onUploaded(res.data);
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Target selector */}
      <Card data-testid="target-selector-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Import Target</CardTitle>
          <CardDescription>What type of data are you importing?</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(targets).map(([key, val]) => (
              <button
                key={key}
                onClick={() => setTarget(key)}
                className={`p-3 rounded-lg border text-sm text-left transition-all ${
                  target === key
                    ? 'border-primary bg-primary/5 font-medium'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`target-${key}`}
              >
                <div className="font-medium">{val.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {Object.keys(val.fields).length} fields
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dropzone */}
      <Card
        className={`border-2 border-dashed transition-colors cursor-pointer ${
          dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
        }`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) handleFile(f);
        }}
        data-testid="csv-dropzone"
      >
        <CardContent className="py-12 text-center">
          {uploading ? (
            <>
              <Loader2 className="h-10 w-10 mx-auto mb-3 animate-spin text-primary" />
              <p className="font-medium">Uploading & parsing file...</p>
            </>
          ) : (
            <>
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Drop your CSV or Excel file here</p>
              <p className="text-sm text-muted-foreground mt-1">
                .csv, .xlsx or .xls · or click to browse (max 10MB)
              </p>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files[0];
              if (f) handleFile(f);
            }}
            data-testid="csv-file-input"
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Step 2: AI Mapping ───
function MappingStep({ upload, targets, onMapped, onBack }) {
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [aiDone, setAiDone] = useState(false);

  const targetFields = targets[upload.target]?.fields || {};

  const runAiMapping = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/data-import/map', { upload_id: upload.upload_id, use_ai: true });
      setMapping(res.data.mapping || {});
      setAiDone(true);
      toast.success('AI mapped your columns!');
    } catch (e) {
      toast.error('AI mapping failed');
    } finally {
      setLoading(false);
    }
  }, [upload.upload_id]);

  const updateField = (csvCol, crmField) => {
    setMapping(prev => ({ ...prev, [csvCol]: crmField || null }));
  };

  const saveMapping = async () => {
    setLoading(true);
    try {
      await api.put('/api/data-import/map', { upload_id: upload.upload_id, mapping });
      onMapped(mapping);
    } catch (e) {
      toast.error('Failed to save mapping');
    } finally {
      setLoading(false);
    }
  };

  const mappedCount = Object.values(mapping).filter(v => v).length;

  return (
    <div className="space-y-4">
      <Card data-testid="mapping-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary" />
                Column Mapping
              </CardTitle>
              <CardDescription>
                {upload.filename} → {targets[upload.target]?.label} ({upload.headers.length} columns)
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {!aiDone && (
                <Button onClick={runAiMapping} disabled={loading} data-testid="ai-map-btn">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
                  AI Auto-Map
                </Button>
              )}
              <Badge variant="outline">
                {mappedCount}/{upload.headers.length} mapped
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {upload.headers.map((header) => (
              <div
                key={header}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/50"
                data-testid={`mapping-row-${header.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium truncate">{header}</span>
                  </div>
                  {upload.sample_rows[0] && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate pl-6">
                      e.g. "{upload.sample_rows[0][header] || '—'}"
                    </p>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="w-48">
                  <select
                    value={mapping[header] || ''}
                    onChange={(e) => updateField(header, e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid={`mapping-select-${header.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <option value="">— Skip —</option>
                    {Object.entries(targetFields).map(([key, field]) => (
                      <option key={key} value={key}>
                        {field.label}{field.required ? ' *' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} data-testid="mapping-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button onClick={saveMapping} disabled={loading || mappedCount === 0} data-testid="mapping-next-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Preview Import <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3: Preview ───
function PreviewStep({ upload, mapping, targets, onConfirm, onBack }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stagePlan, setStagePlan] = useState(null);      // {applicable, plan, new_count, existing_count}
  const [ownerPlan, setOwnerPlan] = useState(null);      // {applicable, owners, matched_owners, unmatched_owners, ...}
  const [showOwners, setShowOwners] = useState(false);
  const [ownerOverrides, setOwnerOverrides] = useState({}); // {csv_owner: {id, name}}
  const [createStages, setCreateStages] = useState(true);
  const [showStages, setShowStages] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const res = await api.post('/api/data-import/preview', { upload_id: upload.upload_id });
        setPreview(res.data);
      } catch (e) {
        toast.error('Failed to load preview');
      } finally {
        setLoading(false);
      }
    };
    const fetchStagePlan = async () => {
      try {
        const res = await api.post('/api/data-import/stage-preview', { upload_id: upload.upload_id });
        setStagePlan(res.data);
        if (res.data?.applicable === false) setCreateStages(false);
      } catch (e) { /* non-blocking */ }
    };
    const fetchOwnerPlan = async () => {
      try {
        const res = await api.post('/api/data-import/owner-preview', { upload_id: upload.upload_id });
        setOwnerPlan(res.data);
      } catch (e) { /* non-blocking */ }
    };
    fetchPreview();
    fetchStagePlan();
    fetchOwnerPlan();
  }, [upload.upload_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="mapping-preview-skeleton">
        <div className="w-full space-y-4">
          <div className="rounded-lg border overflow-hidden">
            <div className="flex gap-4 p-4 border-b bg-muted/30">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-4 flex-1 bg-muted rounded animate-pulse" />
              ))}
            </div>
            {[...Array(3)].map((_, rowIndex) => (
              <div key={rowIndex} className="flex gap-4 p-4 border-b">
                {[...Array(4)].map((_, colIndex) => (
                  <div key={colIndex} className="h-4 flex-1 bg-muted/60 rounded animate-pulse" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const targetFields = targets[upload.target]?.fields || {};
  const mappedFields = Object.values(mapping).filter(v => v);

  return (
    <div className="space-y-4">
      <Card data-testid="preview-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                Import Preview
              </CardTitle>
              <CardDescription>
                {upload.total_rows} rows will be imported into {targets[upload.target]?.label}
              </CardDescription>
            </div>
            {preview?.error_count > 0 && (
              <Badge variant="destructive">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {preview.error_count} error{preview.error_count > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {stagePlan?.applicable && (
            <div className="mb-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-3" data-testid="stage-plan-panel">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createStages}
                  onChange={(e) => setCreateStages(e.target.checked)}
                  className="h-4 w-4 mt-0.5 shrink-0 accent-primary"
                  data-testid="create-stages-checkbox"
                />
                <span className="text-sm">
                  <span className="font-medium">Match my statuses to pipeline columns</span>
                  <span className="block text-xs text-muted-foreground mt-0.5" data-testid="stage-plan-summary">
                    {stagePlan.message} Leads will land in the right column instead of all under "New Lead".
                  </span>
                </span>
              </label>
              {(stagePlan.plan || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowStages((s) => !s)}
                  className="mt-2 ml-6 text-xs text-primary underline"
                  data-testid="stage-plan-toggle"
                >
                  {showStages ? 'Hide' : 'Review'} column mapping ({stagePlan.plan.length})
                </button>
              )}
              {showStages && (
                <div className="mt-2 ml-6 max-h-44 overflow-auto rounded border bg-background divide-y" data-testid="stage-plan-list">
                  {stagePlan.plan.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs" data-testid={`stage-plan-row-${i}`}>
                      <span className="truncate">{p.status || '(blank)'} <span className="text-muted-foreground">· {p.count} lead(s)</span></span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className="text-muted-foreground">→ {p.stage}</span>
                        {p.action === 'create' ? (
                          <span className="text-[10px] font-medium text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">New</span>
                        ) : (
                          <span className="text-[10px] font-medium text-muted-foreground bg-muted rounded px-1.5 py-0.5">Existing</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {ownerPlan?.applicable && (
            <div
              className={`mb-3 rounded-md border px-3 py-3 ${ownerPlan.unmatched_owners > 0 ? 'border-amber-500/30 bg-amber-500/10' : 'border-emerald-500/30 bg-emerald-500/10'}`}
              data-testid="owner-plan-panel"
            >
              <div className="flex items-start gap-2.5">
                {ownerPlan.unmatched_owners > 0
                  ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                  : <CheckCircle className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />}
                <span className="text-sm">
                  <span className="font-medium">Teammate assignment check</span>
                  <span className={`block text-xs mt-0.5 ${ownerPlan.unmatched_owners > 0 ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="owner-plan-summary">
                    {ownerPlan.message}
                    {ownerPlan.unmatched_owners > 0 && ' Tip: invite the missing teammates (with the exact name from your sheet) before importing, or assign these leads later with Bulk Assign.'}
                  </span>
                </span>
              </div>
              {(ownerPlan.owners || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowOwners((s) => !s)}
                  className="mt-2 ml-6 text-xs text-primary underline"
                  data-testid="owner-plan-toggle"
                >
                  {showOwners ? 'Hide' : 'Review'} owner matches ({ownerPlan.owners.length})
                </button>
              )}
              {showOwners && (
                <div className="mt-2 ml-6 max-h-44 overflow-auto rounded border bg-background divide-y" data-testid="owner-plan-list">
                  {ownerPlan.owners.map((o, i) => {
                    const overridden = ownerOverrides[o.owner];
                    return (
                    <div key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs" data-testid={`owner-plan-row-${i}`}>
                      <span className="truncate">{o.owner} <span className="text-muted-foreground">· {o.count} lead(s)</span></span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {o.matched ? (
                          <>
                            <span className="text-muted-foreground">→ {o.assignee}</span>
                            {o.via_alias ? (
                              <span className="text-[10px] font-medium text-violet-600 bg-violet-500/10 border border-violet-500/30 rounded px-1.5 py-0.5">Remembered</span>
                            ) : (
                              <span className="text-[10px] font-medium text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">Matched</span>
                            )}
                          </>
                        ) : overridden ? (
                          <>
                            <span className="text-muted-foreground">→ {overridden.name}</span>
                            <span className="text-[10px] font-medium text-blue-600 bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5">Will assign</span>
                            <button
                              type="button"
                              onClick={() => setOwnerOverrides((m) => { const n = { ...m }; delete n[o.owner]; return n; })}
                              className="text-muted-foreground hover:text-foreground"
                              data-testid={`owner-suggest-undo-${i}`}
                              title="Undo"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </>
                        ) : o.suggestion ? (
                          <button
                            type="button"
                            onClick={() => setOwnerOverrides((m) => ({ ...m, [o.owner]: o.suggestion }))}
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5 hover:bg-blue-500/20"
                            data-testid={`owner-suggest-btn-${i}`}
                          >
                            Assign to {o.suggestion.name}?
                          </button>
                        ) : (
                          <span className="text-[10px] font-medium text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">Unassigned</span>
                        )}
                      </span>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {preview?.duplicate_count > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-blue-500/10 border border-blue-500/20 px-3 py-2" data-testid="preview-dedup-summary">
              <RefreshCw className="h-4 w-4 text-blue-500 shrink-0" />
              <p className="text-xs text-blue-600">
                <span className="font-semibold">{preview.duplicate_count}</span> duplicate{preview.duplicate_count > 1 ? 's' : ''} (matching email or phone) will be skipped automatically.
              </p>
            </div>
          )}
          {preview?.phone_corrupted_count > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2" data-testid="preview-phone-corrupted-summary">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
              <p className="text-xs text-amber-600">
                <span className="font-semibold">{preview.phone_corrupted_count}</span> phone number{preview.phone_corrupted_count > 1 ? 's look' : ' looks'} corrupted (e.g. <span className="font-mono">9.1E+11</span> — Excel scientific notation). Re-export the phone column formatted as <span className="font-semibold">Text</span> to keep them.
              </p>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium text-muted-foreground">#</th>
                  {mappedFields.map(f => (
                    <th key={f} className="text-left py-2 px-3 font-medium text-muted-foreground">
                      {targetFields[f]?.label || f}
                    </th>
                  ))}
                  <th className="text-left py-2 px-3 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview?.preview?.map((row) => (
                  <tr key={row.row} className={`border-b ${row.errors.length > 0 ? 'bg-red-500/5' : ''}`} data-testid={`preview-row-${row.row}`}>
                    <td className="py-2 px-3 text-muted-foreground">{row.row}</td>
                    {mappedFields.map(f => (
                      <td key={f} className="py-2 px-3 max-w-[200px] truncate">
                        {row.data[f] || <span className="text-muted-foreground">—</span>}
                      </td>
                    ))}
                    <td className="py-2 px-3">
                      {row.errors.length > 0 ? (
                        <span className="text-xs text-red-500">{row.errors[0]}</span>
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} data-testid="preview-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Adjust Mapping
        </Button>
        <Button onClick={() => onConfirm(createStages, Object.fromEntries(Object.entries(ownerOverrides).map(([k, v]) => [k, v.id])))} data-testid="execute-import-btn">
          <Play className="h-4 w-4 mr-2" />
          Import {upload.total_rows} Rows
        </Button>
      </div>
    </div>
  );
}

// ─── Step 4: Import ───
function ImportStep({ upload, createStages, ownerOverrides, onDone }) {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(true);
  const [progress, setProgress] = useState(0);
  const [processed, setProcessed] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invoke — otherwise execute is
    // POSTed twice and the 2nd call races/flags every just-inserted row as a duplicate.
    if (startedRef.current) return;
    startedRef.current = true;
    const run = async () => {
      try {
        const res = await api.post('/api/data-import/execute', { upload_id: upload.upload_id, skip_errors: true, create_stages: !!createStages, owner_overrides: ownerOverrides || {} });
        if (res.data?.background) {
          // 3.1: import now runs on the durable background worker — poll for live progress.
          const upId = upload.upload_id;
          for (let i = 0; i < 600; i++) {
            await new Promise((r) => setTimeout(r, 1500));
            let d;
            try {
              const s = await api.get(`/api/data-import/status/${upId}`);
              d = s.data;
            } catch (_e) { continue; }
            setProgress(d.percent || 0);
            setProcessed(d.progress?.processed || 0);
            if (d.status === 'completed') {
              setResult({ status: 'completed', imported: d.imported, skipped: d.skipped,
                duplicates_skipped: d.duplicates_skipped || 0, total: d.total_rows,
                services_created: d.services_created || 0, followups_created: d.followups_created || 0,
                notes_created: d.notes_created || 0 });
              toast.success('Import completed!');
              break;
            }
            if (d.status === 'failed') {
              setResult({ status: 'failed', error: d.error || 'Import failed' });
              toast.error('Import failed', { description: d.error || 'Please try again.' });
              break;
            }
          }
        } else {
          setResult(res.data);
          toast.success('Import completed!');
        }
      } catch (e) {
        const detail = e.response?.data?.detail;
        const errMsg = typeof detail === 'string' ? detail : (detail?.message || 'Network error');
        toast.error('Import failed', { description: errMsg });
        setResult({ status: 'failed', error: errMsg });
      } finally {
        setRunning(false);
      }
    };
    run();
  }, [upload.upload_id]);

  return (
    <Card data-testid="import-result-card">
      <CardContent className="py-12 text-center">
        {running ? (
          <>
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
            <p className="font-semibold text-lg" data-testid="import-progress-label">Importing data…</p>
            <p className="text-sm text-muted-foreground mt-1" data-testid="import-progress-count">
              {processed > 0 ? `${processed.toLocaleString()} of ${(upload.total_rows || 0).toLocaleString()} rows` : 'Starting…'}
            </p>
            <Progress value={progress || 5} className="h-2 mt-4 max-w-md mx-auto" data-testid="import-progress-bar" />
            <p className="text-xs text-muted-foreground mt-2">{progress}%</p>
          </>
        ) : result?.status === 'completed' ? (
          <>
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p className="font-semibold text-lg">Import Complete!</p>
            <div className="flex justify-center gap-6 mt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{result.imported}</p>
                <p className="text-xs text-muted-foreground">Imported</p>
              </div>
              <div className="text-center" data-testid="result-duplicates">
                <p className="text-2xl font-bold text-blue-500">{result.duplicates_skipped || 0}</p>
                <p className="text-xs text-muted-foreground">Duplicates</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">{result.skipped}</p>
                <p className="text-xs text-muted-foreground">Skipped</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-muted-foreground">{result.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
            {(result.services_created > 0 || result.followups_created > 0 || result.notes_created > 0) && (
              <div className="flex justify-center gap-6 mt-3" data-testid="result-extras">
                <div className="text-center">
                  <p className="text-xl font-bold text-purple-500">{result.services_created || 0}</p>
                  <p className="text-xs text-muted-foreground">Services linked</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-indigo-500">{result.followups_created || 0}</p>
                  <p className="text-xs text-muted-foreground">Follow-ups</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-teal-500">{result.notes_created || 0}</p>
                  <p className="text-xs text-muted-foreground">Notes</p>
                </div>
              </div>
            )}
            {result.error_rows?.length > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 max-w-md mx-auto text-left">
                <p className="text-sm font-medium text-amber-600 mb-1">Skipped Rows:</p>
                {result.error_rows.slice(0, 3).map((er, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    Row {er.row}: {er.error || 'Validation error'}
                  </p>
                ))}
              </div>
            )}
            <Button className="mt-6" onClick={onDone} data-testid="import-done-btn">
              Start New Import
            </Button>
          </>
        ) : (
          <>
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <p className="font-semibold text-lg">Import Failed</p>
            <p className="text-sm text-muted-foreground mt-1">{result?.error}</p>
            <Button className="mt-6" variant="outline" onClick={onDone}>
              Try Again
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Import History ───
function ImportHistory({ history, onRefresh, onDelete, onUndo }) {
  if (!history || history.length === 0) {
    return (
      <Card data-testid="import-history-empty">
        <CardContent className="py-8 text-center text-muted-foreground">
          <Clock className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>No import history yet</p>
          <p className="text-sm">Past imports will appear here</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="import-history-list">
      {history.map((imp) => (
        <Card key={imp.id} className="border-border/50" data-testid={`history-${imp.id}`}>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                  imp.status === 'completed' ? 'bg-green-500/10' : 'bg-amber-500/10'
                }`}>
                  {imp.status === 'completed' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clock className="h-4 w-4 text-amber-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium">{imp.filename}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-xs">{imp.target}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {imp.status === 'undone'
                        ? `Undone — ${imp.undone_count || 0} lead(s) removed`
                        : imp.status === 'completed'
                        ? `${imp.imported_count || 0} imported, ${imp.skipped_count || 0} skipped`
                        : imp.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {imp.created_at ? new Date(imp.created_at).toLocaleDateString() : ''}
                </span>
                {imp.status === 'completed' && imp.target === 'leads' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => onUndo(imp.id, imp.imported_count || 0)}
                    data-testid={`undo-history-${imp.id}`}
                    title="Undo this import (removes the leads it added)"
                  >
                    <Undo2 className="h-3 w-3 mr-1" /> Undo
                  </Button>
                )}
                {imp.status === 'undone' && (
                  <Badge variant="outline" className="text-xs text-muted-foreground" data-testid={`undone-badge-${imp.id}`}>Undone</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-500"
                  onClick={() => onDelete(imp.id)}
                  data-testid={`delete-history-${imp.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Main Page ───
export default function DataImportPage() {
  const [targets, setTargets] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [step, setStep] = useState(0); // 0=upload, 1=map, 2=preview, 3=import
  const [createStages, setCreateStages] = useState(true);
  const [ownerOverrides, setOwnerOverrides] = useState({});
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, hRes] = await Promise.all([
        api.get('/api/data-import/targets').catch(() => ({ data: {} })),
        api.get('/api/data-import/history').catch(() => ({ data: {} })),
      ]);
      setTargets(tRes.data?.targets || {});
      setHistory(hRes.data?.imports || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/data-import/${id}`);
      toast.success('Import record deleted');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleUndo = async (id, count) => {
    if (!window.confirm(`Undo this import? This permanently removes the ${count || ''} lead(s) it added.`)) return;
    const tId = toast.loading('Undoing import…');
    try {
      const r = await api.post(`/api/data-import/${id}/undo`);
      toast.success(r.data?.message || 'Import undone', { id: tId });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed', { id: tId });
    }
  };

  const resetWizard = () => {
    setStep(0);
    setUpload(null);
    setMapping({});
    fetchData();
  };

  const stepLabels = ['Upload CSV', 'Map Columns', 'Preview', 'Import'];

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Download className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Data Import</h1>
        </div>
        <div className="space-y-4 py-8">
          <div className="flex gap-4 p-4 border-b bg-muted/30 rounded-t-lg border">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-5 flex-1 bg-muted rounded animate-pulse" />
            ))}
          </div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="p-4 border rounded-lg space-y-2">
              <div className="h-4 w-48 bg-muted rounded animate-pulse" />
              <div className="h-3 w-full bg-muted/40 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="data-import-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Download className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Data Import</h1>
            <p className="text-sm text-muted-foreground">
              Import data from CSV files with AI-powered column mapping
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="import" className="space-y-4">
        <TabsList data-testid="import-tabs">
          <TabsTrigger value="import" data-testid="tab-import">
            <Upload className="h-4 w-4 mr-1" /> New Import
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-1" /> History ({history.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {stepLabels.map((label, idx) => (
              <React.Fragment key={idx}>
                <div className={`flex items-center gap-1.5 ${
                  idx <= step ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                    idx < step ? 'bg-primary text-white border-primary' :
                    idx === step ? 'border-primary text-primary' :
                    'border-muted-foreground/30'
                  }`}>
                    {idx < step ? <CheckCircle className="h-4 w-4" /> : idx + 1}
                  </div>
                  <span className="text-xs font-medium hidden sm:inline">{label}</span>
                </div>
                {idx < stepLabels.length - 1 && (
                  <div className={`flex-1 h-0.5 ${idx < step ? 'bg-primary' : 'bg-muted-foreground/20'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Steps */}
          {step === 0 && (
            <UploadStep
              targets={targets}
              onUploaded={(data) => { setUpload(data); setStep(1); }}
            />
          )}
          {step === 1 && upload && (
            <MappingStep
              upload={upload}
              targets={targets}
              onMapped={(m) => { setMapping(m); setStep(2); }}
              onBack={() => setStep(0)}
            />
          )}
          {step === 2 && upload && (
            <PreviewStep
              upload={upload}
              mapping={mapping}
              targets={targets}
              onConfirm={(cs, overrides) => { setCreateStages(cs); setOwnerOverrides(overrides || {}); setStep(3); }}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && upload && (
            <ImportStep upload={upload} createStages={createStages} ownerOverrides={ownerOverrides} onDone={resetWizard} />
          )}
        </TabsContent>

        <TabsContent value="history">
          <ImportHistory
            history={history}
            onRefresh={fetchData}
            onDelete={handleDelete}
            onUndo={handleUndo}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
